<template>
  <div class="contain callpop-org">
    <div class="callpop-org__head">
      <h3 class="callpop-org__title">{{ text.orgTitle }}</h3>
      <!-- 두 목록이 함께 따른다 — 켠 상태는 URL 질의가 갖는다(새로고침·뒤로 가기에도 남는다) -->
      <el-checkbox class="callpop-org__inactive" :value="includeInactive" @change="setIncludeInactive">{{
        text.includeInactiveLabel
      }}</el-checkbox>
    </div>

    <el-row :gutter="12">
      <el-col :md="9">
        <OperatorList
          :operators="operatorRows"
          :loading="operatorLoading"
          :denied="operatorsDenied"
          :ability="operatorAbility"
          @add="openOperatorForm(null)"
          @edit="openOperatorForm"
          @pick="pickOperator"
          @deactivate="confirmOperatorState($event, 'deactivate')"
          @reactivate="confirmOperatorState($event, 'reactivate')"
          @waive="confirmWaive" />
      </el-col>
      <el-col :md="15">
        <AgencyTable
          :operator="selectedOperator"
          :agencies="agencies"
          :loading="agencyLoading"
          @search="searchAgencies"
          @add="openAgencyForm(null)"
          @edit="openAgencyForm"
          @import="importVisible = true"
          @deactivate="confirmAgencyDeactivate"
          @reactivate="confirmAgencyReactivate"
          @drill-down="goStores" />
      </el-col>
    </el-row>

    <OperatorFormDialog
      :visible.sync="operatorFormVisible"
      :operator="editingOperator"
      :can-set-hq="canSetHq"
      @saved="afterOperatorSaved" />

    <AgencyFormDialog
      :visible.sync="agencyFormVisible"
      :operator="selectedOperator"
      :agency="editingAgency"
      @saved="afterAgencySaved"
      @reactivate="reactivateFromForm" />

    <AgencyImportDialog :visible.sync="importVisible" @imported="afterImport" />

    <StoreDeactivateTransferDialog
      :visible.sync="transferVisible"
      :agency="transferAgency"
      :operators="operatorRows"
      :conflict-message="transferConflict"
      @saved="afterAgencySaved" />
  </div>
</template>

<script>
import AgencyFormDialog from '@/components/callpop/AgencyFormDialog.vue'
import AgencyImportDialog from '@/components/callpop/AgencyImportDialog.vue'
import AgencyTable from '@/components/callpop/AgencyTable.vue'
import OperatorFormDialog from '@/components/callpop/OperatorFormDialog.vue'
import OperatorList from '@/components/callpop/OperatorList.vue'
import StoreDeactivateTransferDialog from '@/components/callpop/StoreDeactivateTransferDialog.vue'
import { permittedRead } from '@/plugins/http/permittedRead'
import { httpErrorMessage, httpStatusOf } from '@/plugins/http/policy'
import { latestGuard } from '@/utils/latestGuard'
import {
  AGENCY_LIST_READ,
  CALLPOP_PATH,
  CALLPOP_WRITE_CONFIG,
  agencyUrl,
  cleanParams,
  includeInactiveOf,
  itemsOf,
  operatorUrl,
  orgListParams,
  withIncludeInactive,
} from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'
import callpopOperatorsMixin from '@/mixins/callpopOperatorsMixin'
import mainVueMixin from '@/mixins/mainVueMixin'

/**
 * **운영사·관리점 관리** (화면 #1, `/v/callpop/org`).
 *
 * 왼쪽에서 운영사를 고르면 오른쪽에 그 아래 관리점이 선다. 범위는 서버가 좁히므로(§3-1) 화면은 받은 것을
 * 그대로 그린다 — 일반 운영사에게는 자기 1행이 오고, 콜팝 관리자·내부에게는 전부 온다.
 *
 * 관리점 비활성은 **두 단계**다: 목적지 없이 한 번 부르고, 409 가 오면 그 문구를 보이며 이관 다이얼로그를
 * 연다(§3-3). 「소속 매장이 있는가」를 화면이 먼저 세지 않는다 — 그 판정은 서버의 것이고, 목록의
 * `storeCount` 는 조회 시점의 값이라 그 사이 바뀔 수 있다.
 *
 * **두 목록은 평소 활성만 보인다**(R-A, E-26 약속) — 「비활성 포함」을 켜면 비활성 행이 흐리게 함께 서고,
 * 재활성은 그때 누른다. 켠 상태는 URL 질의(`?includeInactive=Y`)가 갖는다.
 */
export default {
  name: 'CallpopOrgView',

  mixins: [mainVueMixin, callpopOperatorsMixin],

  components: {
    AgencyFormDialog,
    AgencyImportDialog,
    AgencyTable,
    OperatorFormDialog,
    OperatorList,
    StoreDeactivateTransferDialog,
  },

  data() {
    return {
      text: CALLPOP_TEXT,
      selectedOperator: null,
      agencies: [],
      agencyLoading: false,
      agencyQuery: '',
      /** 늦게 온 응답이 최신 운영사의 관리점 목록을 덮지 못하게(`utils/latestGuard`) */
      agencyGuard: latestGuard(),
      operatorFormVisible: false,
      editingOperator: null,
      agencyFormVisible: false,
      editingAgency: null,
      importVisible: false,
      transferVisible: false,
      transferAgency: null,
      transferConflict: '',
    }
  },

  computed: {
    /**
     * 총괄사 플래그를 만질 수 있는가 — **내부·콜팝 관리자**다(브리프 §2-1).
     *
     * 총괄사 계정도 운영사 쓰기 액션을 갖게 되면서 액션 하나로는 갈리지 않는다(같은 역할에 준 URL 은
     * 일반 운영사에게도 간다) — 그 답은 서버의 `designateHq` 이고, 곱하는 자리는 mixin 하나다.
     *
     * **지금 폼이 부를 리소스의 액션**을 곱한다: 등록은 `POST …`, 수정은 `PUT …/{cd}` 다. 한 값으로
     * 두면 `Up` 만 가진 역할에서 수정 폼의 칸이 등록 액션의 유무로 갈린다(리뷰 §3).
     */
    canSetHq() {
      return this.editingOperator ? this.operatorAbility.upHq : this.operatorAbility.addHq
    },
    /** 「비활성 포함」 — **URL 질의가 정본**이다(화면 상태로 따로 들지 않는다) */
    includeInactive() {
      return includeInactiveOf(this.$route.query)
    },
  },

  watch: {
    // 질의가 바뀌면 두 목록을 다시 읽는다 — 운영사를 먼저 읽고 고른 행의 관리점이 뒤따른다([reloadOperators])
    includeInactive() {
      this.reloadOperators()
    },
  },

  created() {
    this.reloadOperators()
  },

  beforeDestroy() {
    this.agencyGuard.close()
  },

  methods: {
    /**
     * 운영사 목록을 읽고 고른 행을 유지한다 — 쓰기 뒤에도 보던 자리가 바뀌지 않게.
     *
     * 로딩(`operatorLoading`)과 응답 순서는 [loadOperators](`callpopOperatorsMixin`)가 갖는다. 「비활성 포함」을
     * 빠르게 켜고 끄면 조회가 겹치는데, 늦게 온 앞 회차는 거짓으로 끝나므로 고른 행·관리점 조회는 최신만 한다.
     */
    async reloadOperators() {
      const ok = await this.loadOperators(orgListParams(this.includeInactive))
      if (!ok) return

      const keepCd = this.selectedOperator && this.selectedOperator.operatorCd
      const next = this.operatorRows.find((row) => row.operatorCd === keepCd) || this.operatorRows[0] || null

      this.pickOperator(next)
    },

    pickOperator(row) {
      this.selectedOperator = row || null
      this.agencies = []
      if (this.selectedOperator) this.loadAgencies()
    },

    /** 「비활성 포함」을 켜고 끈다 — 질의만 바꾼다. 다시 읽기는 질의를 보는 워처가 한다 */
    setIncludeInactive(on) {
      this.$router.replace({ query: withIncludeInactive(this.$route.query, on) }).catch(() => {})
    },

    searchAgencies(query) {
      this.agencyQuery = query || ''
      this.loadAgencies()
    },

    /**
     * 관리점 목록 — **호출부는 `await` 하지 않아도 된다**(403 은 값으로 접고 그 밖의 실패는 여기서 끝낸다).
     */
    async loadAgencies() {
      if (!this.selectedOperator) return

      const seq = this.agencyGuard.begin()
      this.agencyLoading = true

      try {
        const params = cleanParams(
          Object.assign(
            { operatorCd: this.selectedOperator.operatorCd, q: this.agencyQuery },
            orgListParams(this.includeInactive)
          )
        )
        const outcome = await permittedRead(Object.assign({}, AGENCY_LIST_READ, { params }))
        if (!this.agencyGuard.isLatest(seq)) return

        this.agencies = outcome.allowed ? itemsOf(outcome.data) : []
      } catch (error) {
        if (this.agencyGuard.isLatest(seq)) this.agencies = []
      } finally {
        if (this.agencyGuard.isLatest(seq)) this.agencyLoading = false
      }
    },

    openOperatorForm(row) {
      this.editingOperator = row || null
      this.operatorFormVisible = true
    },

    afterOperatorSaved() {
      this.reloadOperators()
    },

    /**
     * **확인창 한 곳** — 이 화면의 되돌리기 어려운 조작 넷이 같은 모양으로 묻는다.
     *
     * 문구만 다른 확인창을 자리마다 적었더니 **한 자리(관리점 비활성)에만 확인창이 없었다** — 소속
     * 매장을 통째로 옮기는 조작이 클릭 한 번으로 나갔다. 같은 종류의 물음은 같은 함수가 물어야
     * 빠진 자리가 드러난다.
     *
     * @returns {Promise} 사람이 「확인」을 누른 경우에만 resolve — 취소는 그대로 삼킨다
     */
    confirmAction(message, title, confirmText) {
      return this.$confirm(message, title || this.text.confirmTitle, {
        confirmButtonText: confirmText || this.text.confirmAction,
        cancelButtonText: this.text.cancelAction,
        type: 'warning',
      })
    },

    /** 비활성·재활성 — 본문이 없는 두 API 가 확인창 문구만 다르다 */
    confirmOperatorState(row, action) {
      const message = action === 'deactivate' ? this.text.operatorOffConfirm : this.text.operatorOnConfirm

      this.confirmAction(message, this.text.operatorListTitle)
        .then(() => this.putOperator(row, action))
        .catch(() => {})
    },

    confirmWaive(row) {
      this.confirmAction(this.text.operatorWaiveConfirm, this.text.operatorWaiveTitle, this.text.operatorWaiveAction)
        .then(async () => {
          const result = await this.putOperator(row, 'waive')
          if (!result) return
          // 옮겨진 매장 수와 받은 총괄사를 그대로 말한다(§3-2 `{movedStoreCount, toOperator}`)
          const to = (result.toOperator && result.toOperator.name) || ''
          this.$message({
            type: 'success',
            message: `${this.text.operatorWaiveAction} — ${this.text.storeCountColumn} ${
              result.movedStoreCount || 0
            } · ${to}`,
          })
        })
        .catch(() => {})
    },

    async putOperator(row, action) {
      try {
        const response = await this.$axios.put(operatorUrl(row.operatorCd, action), undefined, CALLPOP_WRITE_CONFIG)

        this.reloadOperators()

        return response.data
      } catch (error) {
        // 409(총괄 둘·비활성 불가)·403 은 서버 문장이 곧 답이다 — 인터셉터가 이미 띄웠고 여기서 두 번 말하지 않는다
        return null
      }
    },

    openAgencyForm(row) {
      this.editingAgency = row || null
      this.agencyFormVisible = true
    },

    afterAgencySaved() {
      this.loadAgencies()
      // 관리점 수·매장 수가 바뀌었을 수 있다
      this.reloadOperators()
    },

    afterImport() {
      this.importVisible = false
      this.afterAgencySaved()
    },

    /**
     * 관리점 비활성 — 먼저 목적지 없이 부른다. **409 면 그 문구와 함께 이관 다이얼로그를 연다**(§3-3).
     * `showErrorMessage:false` 로 토스트를 끄는 이유: 그 409 는 이 화면에서 **오류가 아니라 다음 단계**다.
     */
    confirmAgencyDeactivate(row) {
      this.confirmAction(this.text.agencyOffConfirm, this.text.agencyTableTitle, this.text.agencyOffAction)
        .then(() => this.deactivateAgency(row))
        .catch(() => {})
    },

    async deactivateAgency(row) {
      try {
        await this.$axios.put(
          agencyUrl(row.agencyCd, 'deactivate'),
          {},
          Object.assign({ showErrorMessage: false }, CALLPOP_WRITE_CONFIG)
        )

        this.afterAgencySaved()
      } catch (error) {
        if (httpStatusOf(error) === 409) {
          this.transferAgency = row
          this.transferConflict = httpErrorMessage(error, this.text.transferTitle)
          this.transferVisible = true

          return
        }
        // 409 가 아닌 실패는 토스트를 껐으므로 여기서 말한다
        this.$message({ type: 'error', message: httpErrorMessage(error, this.text.loadFailed) })
      }
    },

    /**
     * 관리점 재활성 — 목록 행의 버튼과 등록 폼의 「CJ0001 재활성」(409 `AGENCY_INACTIVE_SAME_MOBILE`)이
     * **같은 확인창·같은 호출**을 지난다.
     *
     * @returns {Promise<boolean>} 재활성했는가 — 취소·실패는 거짓이다
     */
    confirmAgencyReactivate(row) {
      return this.confirmAction(this.text.agencyOnConfirm, this.text.agencyTableTitle)
        .then(async () => {
          try {
            await this.$axios.put(agencyUrl(row.agencyCd, 'reactivate'), undefined, CALLPOP_WRITE_CONFIG)
            this.afterAgencySaved()

            return true
          } catch (error) {
            // 400(비활성 운영사)·409(휴대폰 유일)는 서버 문장이 답이다
            return false
          }
        })
        .catch(() => false)
    },

    /**
     * 등록 폼이 가리킨 비활성 관리점을 되살린다(R-A §1-3) — 새로 만들지 않고 그 관리점을 쓰는 길이다.
     * 되살렸으면 등록 폼은 할 일이 끝났으므로 닫는다. 목록은 [confirmAgencyReactivate] 가 다시 읽는다.
     */
    async reactivateFromForm(target) {
      if (await this.confirmAgencyReactivate(target)) this.agencyFormVisible = false
    },

    /** 소속 매장 수를 누르면 매장 입점 현황을 그 관리점 필터로 연다(§2 #1 드릴다운) */
    goStores(row) {
      this.$router.push({ path: CALLPOP_PATH.STORES, query: { agencyCd: String(row.agencyCd) } })
    },
  },
}
</script>

<style scoped>
.callpop-org__head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}
.callpop-org__title {
  font-size: 16px;
  margin: 0;
}
</style>
